import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * Shared hamburger menu for landing and marketing pages.
 * Shows navigation links for all public pages, plus app tabs if logged in.
 */
object MarketingMenu {
    private val isFrench get() = LocaleManager.getCurrentLocale().code == "fr"

    /**
     * localStorage key for the profile picture, scoped to the signed-in user.
     *
     * This used to be a single global "user_avatar" key, so the picture survived
     * a logout and was shown to whoever signed in next on the same browser —
     * one account displaying another's photo. Keyed per user it can't cross over.
     */
    private fun avatarKey(): String {
        val userId = try {
            auth.UserIdManager().getUserId()
        } catch (_: Throwable) {
            ""
        }
        return if (userId.isBlank()) "user_avatar_anon" else "user_avatar_$userId"
    }

    /**
     * Read this user's avatar.
     *
     * Deliberately does NOT touch the legacy global "user_avatar" key. An
     * earlier version deleted it on sight, which destroyed the picture of
     * anyone who had uploaded one before the per-user keys existed — the key
     * WAS their only copy (avatars are local-only, never sent to the server).
     * Scoping the read is already enough to stop one account showing another's
     * photo; the stale key is simply never read again.
     */
    private fun loadAvatar(): String? = kotlinx.browser.localStorage.getItem(avatarKey())

    /**
     * Returns the HTML for the hamburger button (top-right fixed).
     */
    fun hamburgerButtonHtml(): String {
        val t = WebTheme.current
        return if (t.isDark) """
            <div id="marketing-hamburger" style="position: fixed; top: 16px; right: 24px; z-index: 300; cursor: pointer;">
                <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(20,20,20,0.8); border-radius: 8px; backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); border: 1px solid #1f1f1f;">
                    <span style="font-size: 22px; color: #ffffff; line-height: 1;">&#9776;</span>
                </div>
            </div>
        """ else """
            <div id="marketing-hamburger" style="position: fixed; top: 16px; right: 24px; z-index: 300; cursor: pointer;">
                <span style="font-size: 26px; color: ${t.textPrimary}; line-height: 1;">&#9776;</span>
            </div>
        """
    }

    /**
     * Returns the HTML for the slide-in menu overlay.
     * @param currentPage identifier for highlighting (e.g., "guide", "team", "privacy", etc.)
     * @param isLoggedIn whether to show app navigation section
     */
    fun overlayHtml(currentPage: String = "", isLoggedIn: Boolean = false): String {
        val accentColor = "#00d9ff"

        fun menuItem(id: String, label: String, page: String): String {
            val color = if (currentPage == page) accentColor else "#d4d4d4"
            val weight = if (currentPage == page) "600" else "400"
            return """<a id="$id" href="#" style="display: block; padding: 12px 0; color: $color; text-decoration: none; font-size: 16px; font-weight: $weight; transition: color 0.2s;">$label</a>"""
        }

        val guideLabel = StringKey.GUIDE_TITLE.tr()
        val teamLabel = if (isFrench) "Notre \u00e9quipe" else "Our Team"
        val subscribeLabel = if (isFrench) "S'abonner" else "Subscribe"
        val privacyLabel = StringKey.FOOTER_PRIVACY.tr()
        val termsLabel = StringKey.FOOTER_TERMS.tr()
        val securityLabel = StringKey.FOOTER_SECURITY.tr()
        val contactLabel = StringKey.FOOTER_CONTACT.tr()
        val aboutLabel = StringKey.FOOTER_ABOUT.tr()

        // Logged-in users get a single-column three-zone layout (like Revolut/Wise)
        if (isLoggedIn) {
            val transactionsLabel = StringKey.NAV_TRANSACTIONS.tr()
            val dashboardLabel = StringKey.NAV_DASHBOARD.tr()
            val goalsLabel = StringKey.NAV_GOALS.tr()
            val budgetLabel = StringKey.NAV_BUDGET.tr()
            val settingsLabel = StringKey.NAV_SETTINGS.tr()
            val logoutLabel = StringKey.AUTH_LOGOUT.tr()

            val personSvg = """<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="10" r="5" fill="#666"/><path d="M4 25c0-5.523 4.477-10 10-10s10 4.477 10 10" fill="#666"/></svg>"""
            val editSvg = """<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00d9ff" stroke-width="2.5" stroke-linecap="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>"""
            val navIcon = { name: String -> when (name) {
                "transactions" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>"""
                "dashboard" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>"""
                "goals" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>"""
                "budget" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>"""
                else -> ""
            } }

            val t = WebTheme.current
            val panelBg = t.card
            val headerGradient = if (t.isDark) "linear-gradient(180deg, #1c1c20 0%, #151518 100%)" else "linear-gradient(180deg, ${t.background} 0%, ${t.card} 100%)"
            val border = t.border
            val closeBg = if (t.isDark) "#1e1e22" else t.background
            val closeHoverBg = if (t.isDark) "#2a2a2e" else t.border
            val avatarBg = if (t.isDark) "#222228" else t.background
            val avatarBorder = if (t.isDark) "#3a3a40" else t.border
            val editBadgeBg = if (t.isDark) "#222228" else t.background
            val textPrimary = t.textPrimary
            val textSecondary = t.textSecondary
            val hoverBg = if (t.isDark) "#1e1e24" else t.background
            val hoverText = if (t.isDark) "#00d9ff" else t.accentStrong
            val logoSrc = if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"
            val backdropBg = if (t.isDark) "rgba(0,0,0,0.55)" else "rgba(0,0,0,0.2)"
            val panelShadow = if (t.isDark) "-8px 0 40px rgba(0,0,0,0.4)" else "-8px 0 40px rgba(0,0,0,0.08)"
            val logoutColor = t.expenseRed

            return """
                <div id="marketing-menu-overlay" style="display: none; position: fixed; inset: 0; z-index: 400; background: $backdropBg; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);">
                    <div id="marketing-menu-panel" style="position: absolute; top: 0; right: 0; width: 340px; max-width: 90vw; height: 100%; background: $panelBg; border-left: 1px solid $border; display: flex; flex-direction: column; box-shadow: $panelShadow;">

                        <!-- Zone 1: Logo + Profile header -->
                        <div style="padding: 20px 28px 20px; border-bottom: 1px solid $border; background: $headerGradient;">
                            <!-- Top row: Logo + Close -->
                            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;">
                                <a id="menu-logo" href="#" style="display: flex; align-items: center; text-decoration: none;">
                                    <img src="$logoSrc" alt="wiz\u2261r" style="height: 32px;">
                                </a>
                                <div id="marketing-menu-close" style="cursor: pointer; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: $closeBg; border: 1px solid $border; transition: background 0.15s;" onmouseover="this.style.background='$closeHoverBg'" onmouseout="this.style.background='$closeBg'">
                                    <span style="font-size: 18px; color: $textSecondary; line-height: 1;">&times;</span>
                                </div>
                            </div>

                            <!-- Profile row -->
                            <div id="menu-profile" style="display: flex; align-items: center; gap: 16px; cursor: pointer; padding: 4px; border-radius: 12px; transition: background 0.15s;" onmouseover="this.style.background='${if (t.isDark) "rgba(255,255,255,0.04)" else "rgba(0,0,0,0.03)"}'" onmouseout="this.style.background='transparent'">
                                <div style="position: relative; flex-shrink: 0;">
                                    <div id="menu-avatar" style="width: 48px; height: 48px; border-radius: 50%; background: $avatarBg; border: 2px solid $avatarBorder; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                                        $personSvg
                                    </div>
                                    <div style="position: absolute; bottom: -2px; right: -2px; width: 18px; height: 18px; background: $editBadgeBg; border: 1.5px solid $border; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                                        $editSvg
                                    </div>
                                </div>
                                <div>
                                    <div style="color: $textPrimary; font-size: 14px; font-weight: 600;">${settingsLabel}</div>
                                    <div style="color: $textSecondary; font-size: 11px; margin-top: 1px;">${if (isFrench) "Profil et pr\u00e9f\u00e9rences" else "Profile & preferences"}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Zone 2: Primary navigation -->
                        <div style="flex: 1; padding: 20px 12px; overflow-y: auto;">
                            <a id="menu-transactions" href="#" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; color: $textPrimary; text-decoration: none; font-size: 16px; font-weight: 500; border-radius: 10px; transition: all 0.15s; margin-bottom: 2px;" onmouseover="this.style.background='$hoverBg';this.style.color='$hoverText'" onmouseout="this.style.background='transparent';this.style.color='$textPrimary'">${navIcon("transactions")} $transactionsLabel</a>
                            <a id="menu-dashboard" href="#" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; color: $textPrimary; text-decoration: none; font-size: 16px; font-weight: 500; border-radius: 10px; transition: all 0.15s; margin-bottom: 2px;" onmouseover="this.style.background='$hoverBg';this.style.color='$hoverText'" onmouseout="this.style.background='transparent';this.style.color='$textPrimary'">${navIcon("dashboard")} $dashboardLabel</a>
                            <a id="menu-goals" href="#" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; color: $textPrimary; text-decoration: none; font-size: 16px; font-weight: 500; border-radius: 10px; transition: all 0.15s; margin-bottom: 2px;" onmouseover="this.style.background='$hoverBg';this.style.color='$hoverText'" onmouseout="this.style.background='transparent';this.style.color='$textPrimary'">${navIcon("goals")} $goalsLabel</a>
                            <a id="menu-budget" href="#" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; color: $textPrimary; text-decoration: none; font-size: 16px; font-weight: 500; border-radius: 10px; transition: all 0.15s; margin-bottom: 2px;" onmouseover="this.style.background='$hoverBg';this.style.color='$hoverText'" onmouseout="this.style.background='transparent';this.style.color='$textPrimary'">${navIcon("budget")} $budgetLabel</a>

                            <!-- Secondary links -->
                            <div style="margin-top: 28px; padding-top: 16px; border-top: 1px solid $border;">
                                <a id="menu-guide" href="#" style="display: block; padding: 10px 16px; color: $textSecondary; text-decoration: none; font-size: 14px; border-radius: 8px; transition: all 0.15s;" onmouseover="this.style.background='$hoverBg';this.style.color='$textPrimary'" onmouseout="this.style.background='transparent';this.style.color='$textSecondary'">$guideLabel</a>
                                <a id="menu-subscribe" href="#" style="display: block; padding: 10px 16px; color: $textSecondary; text-decoration: none; font-size: 14px; border-radius: 8px; transition: all 0.15s;" onmouseover="this.style.background='$hoverBg';this.style.color='$textPrimary'" onmouseout="this.style.background='transparent';this.style.color='$textSecondary'">$subscribeLabel</a>
                            </div>

                            <!-- Company & Legal grouped -->
                            <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid $border;">
                                <div style="display: flex; gap: 20px; padding: 8px 16px; flex-wrap: wrap;">
                                    <a id="menu-team" href="#" style="color: $textSecondary; text-decoration: none; font-size: 13px; transition: color 0.15s;" onmouseover="this.style.color='$textPrimary'" onmouseout="this.style.color='$textSecondary'">$teamLabel</a>
                                    <a id="menu-about" href="#" style="color: $textSecondary; text-decoration: none; font-size: 13px; transition: color 0.15s;" onmouseover="this.style.color='$textPrimary'" onmouseout="this.style.color='$textSecondary'">$aboutLabel</a>
                                    <a id="menu-contact" href="#" style="color: $textSecondary; text-decoration: none; font-size: 13px; transition: color 0.15s;" onmouseover="this.style.color='$textPrimary'" onmouseout="this.style.color='$textSecondary'">$contactLabel</a>
                                </div>
                                <div style="display: flex; gap: 20px; padding: 6px 16px; flex-wrap: wrap;">
                                    <a id="menu-privacy" href="#" style="color: $textSecondary; text-decoration: none; font-size: 12px; transition: color 0.15s;" onmouseover="this.style.color='$textPrimary'" onmouseout="this.style.color='$textSecondary'">$privacyLabel</a>
                                    <a id="menu-terms" href="#" style="color: $textSecondary; text-decoration: none; font-size: 12px; transition: color 0.15s;" onmouseover="this.style.color='$textPrimary'" onmouseout="this.style.color='$textSecondary'">$termsLabel</a>
                                    <a id="menu-security" href="#" style="color: $textSecondary; text-decoration: none; font-size: 12px; transition: color 0.15s;" onmouseover="this.style.color='$textPrimary'" onmouseout="this.style.color='$textSecondary'">$securityLabel</a>
                                </div>
                            </div>
                        </div>

                        <!-- Zone 3: Logout pinned to bottom -->
                        <div style="padding: 16px 28px 24px; border-top: 1px solid $border;">
                            <a id="menu-logout" href="#" style="display: flex; align-items: center; gap: 10px; padding: 10px 4px; color: $logoutColor; text-decoration: none; font-size: 14px; transition: color 0.15s;">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                                $logoutLabel
                            </a>
                        </div>

                        <!-- Hidden file input for avatar upload -->
                        <input id="menu-avatar-upload" type="file" accept="image/*" style="display: none;">
                    </div>
                </div>
            """
        }

        // Non-logged-in layout (unchanged)
        return """
            <div id="marketing-menu-overlay" style="display: none; position: fixed; inset: 0; z-index: 400; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);">
                <div id="marketing-menu-panel" style="position: absolute; top: 0; right: 0; width: 280px; height: 100%; background: #0a0a0a; border-left: 1px solid #1f1f1f; padding: 24px; overflow-y: auto; box-shadow: -4px 0 20px rgba(0,0,0,0.5);">
                    <!-- Close button -->
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
                        <img id="marketing-menu-logo" src="/wiz3r_logo.png?v=3" alt="wiz\u2261r" style="height: 28px; cursor: pointer;">
                        <div id="marketing-menu-close" style="cursor: pointer; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; background: #141414;">
                            <span style="font-size: 18px; color: #999;">\u2715</span>
                        </div>
                    </div>

                    <!-- Product -->
                    <p style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px;">${StringKey.FOOTER_PRODUCT.tr()}</p>
                    ${menuItem("menu-guide", guideLabel, "guide")}
                    ${menuItem("menu-subscribe", subscribeLabel, "subscribe")}

                    <!-- Company -->
                    <div style="border-top: 1px solid #1f1f1f; margin-top: 8px; padding-top: 16px;">
                        <p style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px;">${StringKey.FOOTER_COMPANY.tr()}</p>
                        ${menuItem("menu-team", teamLabel, "team")}
                        ${menuItem("menu-about", aboutLabel, "about")}
                        ${menuItem("menu-contact", contactLabel, "contact")}
                    </div>

                    <!-- Legal -->
                    <div style="border-top: 1px solid #1f1f1f; margin-top: 8px; padding-top: 16px;">
                        <p style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px;">${StringKey.FOOTER_LEGAL.tr()}</p>
                        ${menuItem("menu-privacy", privacyLabel, "privacy")}
                        ${menuItem("menu-terms", termsLabel, "terms")}
                        ${menuItem("menu-security", securityLabel, "security")}
                    </div>
                </div>
            </div>
        """
    }

    /**
     * Attaches event listeners for the hamburger menu.
     * Call this after the element is in the DOM.
     */
    fun attachEventListeners(
        onNavigate: (String) -> Unit,
        isLoggedIn: Boolean = false,
        onAppNavigate: ((String) -> Unit)? = null,
    ) {
        val overlay = document.getElementById("marketing-menu-overlay") as? HTMLElement ?: return
        val hamburger = document.getElementById("marketing-hamburger") as? HTMLElement
        val closeBtn = document.getElementById("marketing-menu-close") as? HTMLElement
        val panel = document.getElementById("marketing-menu-panel") as? HTMLElement

        fun openMenu() { overlay.style.display = "block" }
        fun closeMenu() { overlay.style.display = "none" }

        val logo = document.getElementById("menu-logo") as? HTMLElement

        hamburger?.addEventListener("click", { openMenu() })
        closeBtn?.addEventListener("click", { closeMenu() })
        logo?.addEventListener("click", { e ->
            e.preventDefault()
            closeMenu()
            onNavigate("landing")
        })

        // Close on backdrop click (outside panel)
        overlay.addEventListener("click", { e ->
            if (e.target == overlay) closeMenu()
        })

        // Navigation items
        val pages = listOf(
            "menu-guide" to "guide",
            "menu-team" to "team",
            "menu-about" to "about",
            "menu-contact" to "contact",
            "menu-subscribe" to "subscribe",
            "menu-privacy" to "privacy",
            "menu-terms" to "terms",
            "menu-security" to "security",
        )
        pages.forEach { (id, page) ->
            document.getElementById(id)?.addEventListener("click", { e ->
                e.preventDefault()
                closeMenu()
                onNavigate(page)
            })
        }

        // App navigation (logged in)
        if (isLoggedIn && onAppNavigate != null) {
            listOf("menu-transactions" to "transactions", "menu-dashboard" to "dashboard",
                "menu-goals" to "goals", "menu-budget" to "budget"
            ).forEach { (id, tab) ->
                document.getElementById(id)?.addEventListener("click", { e ->
                    e.preventDefault()
                    closeMenu()
                    onAppNavigate(tab)
                })
            }

            // Profile -> navigate to settings
            document.getElementById("menu-profile")?.addEventListener("click", { e ->
                e.preventDefault()
                closeMenu()
                onAppNavigate("settings")
            })

            // Avatar upload — clicking the edit icon opens file picker
            val avatarUpload = document.getElementById("menu-avatar-upload") as? org.w3c.dom.HTMLInputElement
            document.getElementById("menu-avatar")?.addEventListener("click", { e ->
                e.stopPropagation() // don't trigger profile click
                avatarUpload?.click()
            })
            avatarUpload?.addEventListener("change", {
                val file = avatarUpload.files?.item(0) ?: return@addEventListener
                val reader = org.w3c.files.FileReader()
                reader.onload = { event ->
                    val dataUrl = event.target.asDynamic().result as? String
                    if (dataUrl != null) {
                        val avatarDiv = document.getElementById("menu-avatar") as? HTMLElement
                        avatarDiv?.innerHTML = "<img src='$dataUrl' style='width:100%;height:100%;object-fit:cover;border-radius:50%;'>"
                        kotlinx.browser.localStorage.setItem(avatarKey(), dataUrl)
                    }
                    Unit
                }
                reader.readAsDataURL(file)
            })
            // Restore saved avatar on load
            val savedAvatar = loadAvatar()
            if (!savedAvatar.isNullOrEmpty()) {
                val avatarDiv = document.getElementById("menu-avatar") as? HTMLElement
                avatarDiv?.innerHTML = "<img src='$savedAvatar' style='width:100%;height:100%;object-fit:cover;border-radius:50%;'>"
            }

            // Logout
            document.getElementById("menu-logout")?.addEventListener("click", { e ->
                e.preventDefault()
                closeMenu()
                onAppNavigate("logout")
            })
        }
    }

    /**
     * Returns the HTML for a full-screen mobile menu overlay (used inside app pages).
     * Matches the desktop three-zone layout but adapted for touch: full width, larger targets.
     */
    fun mobileOverlayHtml(): String {
        val accentColor = "#00d9ff"
        val transactionsLabel = StringKey.NAV_TRANSACTIONS.tr()
        val dashboardLabel = StringKey.NAV_DASHBOARD.tr()
        val goalsLabel = StringKey.NAV_GOALS.tr()
        val budgetLabel = StringKey.NAV_BUDGET.tr()
        val settingsLabel = StringKey.NAV_SETTINGS.tr()
        val logoutLabel = StringKey.AUTH_LOGOUT.tr()
        val guideLabel = StringKey.GUIDE_TITLE.tr()
        val subscribeLabel = if (isFrench) "S'abonner" else "Subscribe"
        val teamLabel = if (isFrench) "Notre \u00e9quipe" else "Our Team"
        val aboutLabel = StringKey.FOOTER_ABOUT.tr()
        val contactLabel = StringKey.FOOTER_CONTACT.tr()
        val privacyLabel = StringKey.FOOTER_PRIVACY.tr()
        val termsLabel = StringKey.FOOTER_TERMS.tr()
        val securityLabel = StringKey.FOOTER_SECURITY.tr()

        val personSvg = """<svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="10" r="5" fill="#666"/><path d="M4 25c0-5.523 4.477-10 10-10s10 4.477 10 10" fill="#666"/></svg>"""
        val editSvg = """<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="$accentColor" stroke-width="2.5" stroke-linecap="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>"""
        val navIcon = { name: String -> when (name) {
            "transactions" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>"""
            "dashboard" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>"""
            "goals" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>"""
            "budget" -> """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>"""
            else -> ""
        } }

        val t = WebTheme.current
        val panelBg = t.card
        val headerGradient = if (t.isDark) "linear-gradient(180deg, #1c1c20 0%, #111114 100%)" else "linear-gradient(180deg, ${t.background} 0%, ${t.card} 100%)"
        val border = t.border
        val closeBg = if (t.isDark) "#1e1e22" else t.background
        val avatarBg = if (t.isDark) "#222228" else t.background
        val avatarBorder = if (t.isDark) "#3a3a40" else t.border
        val editBadgeBg = if (t.isDark) "#222228" else t.background
        val textPrimary = t.textPrimary
        val textSecondary = t.textSecondary
        val logoSrc = if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"
        val backdropBg = if (t.isDark) "rgba(0,0,0,0.55)" else "rgba(0,0,0,0.2)"
        val panelShadow = if (t.isDark) "-4px 0 24px rgba(0,0,0,0.5)" else "-4px 0 24px rgba(0,0,0,0.08)"
        val logoutColor = t.expenseRed
        val settingsGearSvg = """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 -2.83 2.83l-.06-.06a1.65 1.65 0 0 0 -1.82-.33 1.65 1.65 0 0 0 -1 1.51V21a2 2 0 0 1 -4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0 -1.82.33l-.06.06a2 2 0 0 1 -2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0 -1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0 -.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0 -1.51 1z"/></svg>"""

        return """
            <div id="mobileMenuBackdrop" style="display: none; position: fixed; inset: 0; z-index: 199; background: $backdropBg; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);"></div>
            <div id="mobileMenuOverlay" style="display: none; position: fixed; top: 0; right: 0; bottom: 0; left: auto; z-index: 200; background: $panelBg; overflow-y: auto; width: 100%; max-width: 400px; box-shadow: $panelShadow;">
                <!-- Zone 1: Logo + Profile + Close -->
                <div style="padding: 16px 20px 14px; border-bottom: 1px solid $border; background: $headerGradient;">
                    <!-- Top row: Logo + Close -->
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;">
                        <a id="mobile-menu-logo" href="#" style="display: flex; align-items: center; text-decoration: none;">
                            <img src="$logoSrc" alt="wiz\u2261r" style="height: 32px;">
                        </a>
                        <div id="mobile-menu-close" style="cursor: pointer; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: $closeBg; border: 1px solid $border;">
                            <span style="font-size: 20px; color: $textSecondary; line-height: 1;">&times;</span>
                        </div>
                    </div>
                    <!-- Profile row -->
                    <div id="mobile-menu-profile" style="display: flex; align-items: center; gap: 14px; cursor: pointer; padding: 4px; border-radius: 12px;">
                        <div style="position: relative; flex-shrink: 0;">
                            <div id="mobile-menu-avatar" style="width: 42px; height: 42px; border-radius: 50%; background: $avatarBg; border: 2px solid $avatarBorder; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                                $personSvg
                            </div>
                            <div style="position: absolute; bottom: -2px; right: -2px; width: 16px; height: 16px; background: $editBadgeBg; border: 1.5px solid $border; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                                $editSvg
                            </div>
                        </div>
                        <div>
                            <div style="color: $textPrimary; font-size: 14px; font-weight: 600;">$settingsLabel</div>
                            <div style="color: $textSecondary; font-size: 11px; margin-top: 1px;">${if (isFrench) "Profil et pr\u00e9f\u00e9rences" else "Profile & preferences"}</div>
                        </div>
                    </div>
                </div>

                <!-- Zone 2: Primary navigation -->
                <div style="padding: 16px 12px;">
                    <a id="mobile-menu-transactions" href="#" style="display: flex; align-items: center; gap: 14px; padding: 16px 16px; color: $textPrimary; text-decoration: none; font-size: 17px; font-weight: 500; border-radius: 10px; min-height: 48px;">${navIcon("transactions")} $transactionsLabel</a>
                    <a id="mobile-menu-dashboard" href="#" style="display: flex; align-items: center; gap: 14px; padding: 16px 16px; color: $textPrimary; text-decoration: none; font-size: 17px; font-weight: 500; border-radius: 10px; min-height: 48px;">${navIcon("dashboard")} $dashboardLabel</a>
                    <a id="mobile-menu-goals" href="#" style="display: flex; align-items: center; gap: 14px; padding: 16px 16px; color: $textPrimary; text-decoration: none; font-size: 17px; font-weight: 500; border-radius: 10px; min-height: 48px;">${navIcon("goals")} $goalsLabel</a>
                    <a id="mobile-menu-budget" href="#" style="display: flex; align-items: center; gap: 14px; padding: 16px 16px; color: $textPrimary; text-decoration: none; font-size: 17px; font-weight: 500; border-radius: 10px; min-height: 48px;">${navIcon("budget")} $budgetLabel</a>
                    <a id="mobile-menu-settings" href="#" style="display: flex; align-items: center; gap: 14px; padding: 16px 16px; color: $textPrimary; text-decoration: none; font-size: 17px; font-weight: 500; border-radius: 10px; min-height: 48px;">$settingsGearSvg $settingsLabel</a>

                    <!-- Secondary links -->
                    <div style="margin-top: 20px; padding-top: 14px; border-top: 1px solid $border;">
                        <a id="mobile-menu-guide" href="#" style="display: block; padding: 12px 16px; color: $textSecondary; text-decoration: none; font-size: 15px; border-radius: 8px; min-height: 44px; display: flex; align-items: center;">$guideLabel</a>
                        <a id="mobile-menu-subscribe" href="#" style="display: block; padding: 12px 16px; color: $textSecondary; text-decoration: none; font-size: 15px; border-radius: 8px; min-height: 44px; display: flex; align-items: center;">$subscribeLabel</a>
                    </div>

                    <!-- Company & Legal -->
                    <div style="margin-top: 14px; padding-top: 14px; border-top: 1px solid $border;">
                        <div style="display: flex; gap: 20px; padding: 10px 16px; flex-wrap: wrap;">
                            <a id="mobile-menu-team" href="#" style="color: $textSecondary; text-decoration: none; font-size: 13px;">$teamLabel</a>
                            <a id="mobile-menu-about" href="#" style="color: $textSecondary; text-decoration: none; font-size: 13px;">$aboutLabel</a>
                            <a id="mobile-menu-contact" href="#" style="color: $textSecondary; text-decoration: none; font-size: 13px;">$contactLabel</a>
                        </div>
                        <div style="display: flex; gap: 20px; padding: 8px 16px; flex-wrap: wrap;">
                            <a id="mobile-menu-privacy" href="#" style="color: $textSecondary; text-decoration: none; font-size: 12px;">$privacyLabel</a>
                            <a id="mobile-menu-terms" href="#" style="color: $textSecondary; text-decoration: none; font-size: 12px;">$termsLabel</a>
                            <a id="mobile-menu-security" href="#" style="color: $textSecondary; text-decoration: none; font-size: 12px;">$securityLabel</a>
                        </div>
                    </div>
                </div>

                <!-- Zone 3: Logout pinned to bottom -->
                <div style="padding: 16px 28px 32px; border-top: 1px solid $border; margin-top: auto;">
                    <a id="mobile-menu-logout" href="#" style="display: flex; align-items: center; gap: 10px; padding: 12px 4px; color: $logoutColor; text-decoration: none; font-size: 15px; min-height: 48px;">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        $logoutLabel
                    </a>
                </div>

                <!-- Hidden file input for avatar upload -->
                <input id="mobile-menu-avatar-upload" type="file" accept="image/*" style="display: none;">
            </div>
        """
    }

    /**
     * Attaches event listeners for the mobile menu overlay (used inside app pages).
     * @param container the parent element containing the mobileMenuOverlay
     * @param onNavigate callback for marketing/legal page navigation (page string like "guide", "privacy")
     * @param onAppNavigate callback for app tab navigation (tab string like "transactions", "settings", "logout")
     */
    fun attachMobileEventListeners(
        container: HTMLElement,
        onNavigate: (String) -> Unit,
        onAppNavigate: (String) -> Unit,
    ) {
        val overlay = container.querySelector("#mobileMenuOverlay") as? HTMLElement ?: return
        val backdrop = container.querySelector("#mobileMenuBackdrop") as? HTMLElement

        fun closeMenu() {
            overlay.style.display = "none"
            backdrop?.style?.display = "none"
        }

        // Close button
        container.querySelector("#mobile-menu-close")?.addEventListener("click", { closeMenu() })

        // Close on backdrop click
        backdrop?.addEventListener("click", { closeMenu() })

        // Logo -> landing page
        container.querySelector("#mobile-menu-logo")?.addEventListener("click", { e ->
            e.preventDefault()
            closeMenu()
            onNavigate("landing")
        })

        // Primary app nav
        listOf(
            "mobile-menu-transactions" to "transactions",
            "mobile-menu-dashboard" to "dashboard",
            "mobile-menu-goals" to "goals",
            "mobile-menu-budget" to "budget",
            "mobile-menu-settings" to "settings",
        ).forEach { (id, tab) ->
            container.querySelector("#$id")?.addEventListener("click", { e ->
                e.preventDefault()
                closeMenu()
                onAppNavigate(tab)
            })
        }

        // Secondary / marketing navigation
        listOf(
            "mobile-menu-guide" to "guide",
            "mobile-menu-subscribe" to "subscribe",
            "mobile-menu-team" to "team",
            "mobile-menu-about" to "about",
            "mobile-menu-contact" to "contact",
            "mobile-menu-privacy" to "privacy",
            "mobile-menu-terms" to "terms",
            "mobile-menu-security" to "security",
        ).forEach { (id, page) ->
            container.querySelector("#$id")?.addEventListener("click", { e ->
                e.preventDefault()
                closeMenu()
                onNavigate(page)
            })
        }

        // Profile -> settings
        container.querySelector("#mobile-menu-profile")?.addEventListener("click", { e ->
            e.preventDefault()
            closeMenu()
            onAppNavigate("settings")
        })

        // Avatar upload — clicking the avatar opens file picker
        val avatarUpload = container.querySelector("#mobile-menu-avatar-upload") as? org.w3c.dom.HTMLInputElement
        container.querySelector("#mobile-menu-avatar")?.addEventListener("click", { e ->
            e.stopPropagation()
            avatarUpload?.click()
        })
        avatarUpload?.addEventListener("change", {
            val file = avatarUpload.files?.item(0) ?: return@addEventListener
            val reader = org.w3c.files.FileReader()
            reader.onload = { event ->
                val dataUrl = event.target.asDynamic().result as? String
                if (dataUrl != null) {
                    val avatarDiv = container.querySelector("#mobile-menu-avatar") as? HTMLElement
                    avatarDiv?.innerHTML = "<img src='$dataUrl' style='width:100%;height:100%;object-fit:cover;border-radius:50%;'>"
                    kotlinx.browser.localStorage.setItem(avatarKey(), dataUrl)
                }
                Unit
            }
            reader.readAsDataURL(file)
        })
        // Restore saved avatar
        val savedAvatar = loadAvatar()
        if (!savedAvatar.isNullOrEmpty()) {
            val avatarDiv = container.querySelector("#mobile-menu-avatar") as? HTMLElement
            avatarDiv?.innerHTML = "<img src='$savedAvatar' style='width:100%;height:100%;object-fit:cover;border-radius:50%;'>"
        }

        // Logout
        container.querySelector("#mobile-menu-logout")?.addEventListener("click", { e ->
            e.preventDefault()
            closeMenu()
            onAppNavigate("logout")
        })

    }
}
